import {
  format,
  formatDate,
  isValid,
  parse,
  parseISO,
  startOfDay,
} from "date-fns";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses date strings as local calendar dates. Avoids `new Date("yyyy-MM-dd")`,
 * which is interpreted as UTC midnight and can shift the day in local timezones.
 */
export const parseLocalDateString = (value?: string | null): Date | null => {
  if (!value?.trim()) return null;

  const trimmed = value.trim();

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    const parsed = parse(trimmed, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed : null;
  }

  const parsed = parseISO(trimmed);
  return isValid(parsed) ? parsed : null;
};

/**
 * Formats a calendar date as `yyyy-MM-dd`.
 *
 * Use this — never `formatLocalISOString` — for date-only fields. The `xxx` offset token only
 * prints down to the minute, so any sub-minute zone offset gets truncated and the value lands
 * on the previous day when it is parsed back. `Europe/Lisbon` is LMT `-00:36:45` for every
 * date before 1912, which is exactly what MUI's partial years (`0002`, `0020`, `0202`, ...)
 * hit while the user types. A bare `yyyy-MM-dd` carries no offset, so it cannot drift — in any
 * timezone, on either side of a DST boundary.
 */
export const formatLocalDateString = (date?: Date | null): string => {
  if (!date || !isValid(date)) return "";
  return format(date, "yyyy-MM-dd");
};

/**
 * Reduces any date or datetime string to its `yyyy-MM-dd` part.
 *
 * Defensive normalization for values that arrive from outside the app (API payloads, stored
 * form state) and are headed for a date-only field. Returns "" when there is no leading
 * calendar date to keep.
 */
export const toLocalDateOnly = (value?: string | null): string => {
  const trimmed = value?.trim();
  if (!trimmed) return "";

  const match = /^\d{4}-\d{2}-\d{2}/.exec(trimmed);
  return match ? match[0] : "";
};

/**
 * Earliest accepted birthdate. Below any living person or animal, and above every partial year
 * MUI emits mid-typing (`0001`-`0202`), so a half-typed year can never be saved.
 */
export const MIN_BIRTHDATE = new Date(1900, 0, 1);

/** Today at local midnight — the upper bound for a birthdate. */
export const getTodayLocalDate = (): Date => startOfDay(new Date());

/** For genuine timestamps only. Date-only fields must use `formatLocalDateString`. */
export const formatLocalISOString = (date: Date): string =>
  format(date, "yyyy-MM-dd'T'HH:mm:ssxxx");

export const GetLocalStartOfDayISOString = (dt: Date | null) => {
  if (!dt) return "";
  return format(
    new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0, 0, 0),
    "yyyy-MM-dd'T'HH:mm:ssxxx",
  );
};

export const GetLocalEndOfDayISOString = (dt: Date | null) => {
  if (!dt) return "";
  return format(
    new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 23, 59, 59, 0),
    "yyyy-MM-dd'T'HH:mm:ssxxx",
  );
};

export const ceilToFiveMinutes = (date: Date): Date => {
  const slotMs = 5 * 60 * 1000;
  const rounded = Math.ceil(date.getTime() / slotMs) * slotMs;
  const result = new Date(rounded);
  result.setSeconds(0, 0);
  return result;
};

export const addMinutes = (date: Date, minutes: number): Date =>
  new Date(date.getTime() + minutes * 60 * 1000);

export const getTimeFromISOString = (isoString: string): string => {
  if (!isoString) return "";
  return formatDate(parseISO(isoString), "HH:mm");
};
